import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  ArrowLeft, Check, ShoppingCart, Package, Clock, Link as LinkIcon,
  FileText, Star, Shield, Zap, Gift, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const bundleTypeConfig = {
  publication_bundle: { label: 'Publication Bundle', color: 'bg-blue-500', icon: FileText },
  press_release_bundle: { label: 'Press Release', color: 'bg-purple-500', icon: Zap },
  social_bundle: { label: 'Social Media Bundle', color: 'bg-pink-500', icon: Gift },
  print_bundle: { label: 'Print Media Bundle', color: 'bg-amber-500', icon: Package },
  cross_service: { label: 'Cross-Service Bundle', color: 'bg-green-500', icon: Star },
};

const BundleDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBundleDetails();
  }, [slug]);

  const fetchBundleDetails = async () => {
    try {
      // Try to fetch by slug first, then by ID
      const response = await axios.get(`${API}/bundles`);
      const foundBundle = response.data.find(b => b.slug === slug || b.id === slug);
      
      if (foundBundle) {
        setBundle(foundBundle);
      }
    } catch (error) {
      console.error('Error fetching bundle:', error);
      toast.error('Failed to load bundle details');
    } finally {
      setLoading(false);
    }
  };

  const handleBuyNow = () => {
    navigate('/checkout', { 
      state: { 
        itemType: 'bundle',
        bundle: bundle
      } 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fafafa]">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!bundle) {
    return (
      <div className="min-h-screen bg-[#fafafa]">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 text-center">
          <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h1 className="font-playfair text-2xl font-bold mb-2">Bundle Not Found</h1>
          <p className="text-[#737373] mb-6">The bundle you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/marketplace?type=bundles')}
            className="px-6 py-3 bg-[#D4AF37] text-[#0d0d0d] rounded-lg hover:bg-[#B8960F]"
          >
            Browse Bundles
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const typeConfig = bundleTypeConfig[bundle.bundle_type] || bundleTypeConfig.publication_bundle;
  const TypeIcon = typeConfig.icon;
  const discountPercent = bundle.discount_percent || 
    (bundle.original_price > 0 ? Math.round((1 - bundle.bundle_price / bundle.original_price) * 100) : 0);

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="bundle-detail">
      <Helmet>
        <title>{bundle.name} - Bundle Package | BusinessMint PR</title>
        <meta name="description" content={bundle.short_description || bundle.description} />
      </Helmet>
      <Header />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/marketplace?type=bundles')}
          className="flex items-center gap-2 text-[#737373] hover:text-[#1a1a1a] mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="font-manrope text-sm">Back to Bundles</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Bundle Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Card */}
            <div className="bg-white border border-[#e5e5e5] rounded-xl overflow-hidden">
              {/* Hero Image */}
              <div className="relative h-64 bg-gradient-to-br from-[#1a1a1a] to-[#404040]">
                {bundle.image ? (
                  <img src={bundle.image} alt={bundle.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <TypeIcon className="w-24 h-24 text-white/20" />
                  </div>
                )}
                {discountPercent > 0 && (
                  <div className="absolute top-4 right-4 bg-[#D4AF37] text-[#0d0d0d] px-4 py-2 rounded-lg font-bold text-lg">
                    {discountPercent}% OFF
                  </div>
                )}
              </div>
              
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${typeConfig.color}`}>
                    {typeConfig.label}
                  </span>
                  {bundle.is_featured && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 flex items-center gap-1">
                      <Star className="w-3 h-3" />
                      Featured
                    </span>
                  )}
                </div>
                
                <h1 className="font-playfair text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">{bundle.name}</h1>
                
                {bundle.short_description && (
                  <p className="font-manrope text-lg text-[#737373] mb-6">{bundle.short_description}</p>
                )}

                {/* Key Stats */}
                <div className="flex flex-wrap gap-6 pt-6 border-t border-[#e5e5e5]">
                  {bundle.guaranteed_links && (
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-[#fee2e2] rounded-lg">
                        <LinkIcon className="w-5 h-5 text-[#D4AF37]" />
                      </div>
                      <div>
                        <p className="font-mono text-2xl font-bold text-[#1a1a1a]">{bundle.guaranteed_links}+</p>
                        <p className="text-xs text-[#737373] uppercase tracking-widest">Links Guaranteed</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-[#dbeafe] rounded-lg">
                      <Clock className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-mono text-2xl font-bold text-[#1a1a1a]">
                        {bundle.turnaround_days_min === bundle.turnaround_days_max 
                          ? bundle.turnaround_days_min 
                          : `${bundle.turnaround_days_min}-${bundle.turnaround_days_max}`}
                      </p>
                      <p className="text-xs text-[#737373] uppercase tracking-widest">Days Turnaround</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            {bundle.description && (
              <div className="bg-white border border-[#e5e5e5] rounded-xl p-6">
                <h2 className="font-playfair text-xl font-bold mb-4 text-[#1a1a1a]">About This Bundle</h2>
                <p className="font-manrope text-[#737373] leading-relaxed whitespace-pre-line">{bundle.description}</p>
              </div>
            )}

            {/* Features */}
            {bundle.features && bundle.features.length > 0 && (
              <div className="bg-white border border-[#e5e5e5] rounded-xl p-6">
                <h2 className="font-playfair text-xl font-bold mb-4 text-[#1a1a1a]">What's Included</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {bundle.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-[#f5f5f5] rounded-lg">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="font-manrope text-sm text-[#1a1a1a]">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Items in Bundle */}
            {bundle.items && bundle.items.length > 0 && (
              <div className="bg-white border border-[#e5e5e5] rounded-xl p-6">
                <h2 className="font-playfair text-xl font-bold mb-4 text-[#1a1a1a]">Bundle Contents</h2>
                <div className="space-y-2">
                  {bundle.items.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-[#f5f5f5] rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center">
                          <span className="font-mono text-sm font-bold text-[#D4AF37]">{idx + 1}</span>
                        </div>
                        <div>
                          <p className="font-semibold text-[#1a1a1a]">{item.item_name}</p>
                          <p className="text-xs text-[#737373] capitalize">{item.item_type?.replace(/_/g, ' ')}</p>
                        </div>
                      </div>
                      {item.quantity > 1 && (
                        <span className="text-sm text-[#737373]">×{item.quantity}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Deliverables */}
            {bundle.deliverable_description && (
              <div className="bg-white border border-[#e5e5e5] rounded-xl p-6">
                <h2 className="font-playfair text-xl font-bold mb-4 text-[#1a1a1a]">Deliverables</h2>
                <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <FileText className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="font-manrope text-green-800">{bundle.deliverable_description}</p>
                </div>
                {bundle.sample_report_url && (
                  <a 
                    href={bundle.sample_report_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-4 text-[#D4AF37] hover:underline font-manrope text-sm"
                  >
                    <FileText className="w-4 h-4" />
                    View Sample Report
                    <ArrowRight className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}

            {/* Categories */}
            {bundle.categories && bundle.categories.length > 0 && (
              <div className="bg-white border border-[#e5e5e5] rounded-xl p-6">
                <h2 className="font-playfair text-xl font-bold mb-4 text-[#1a1a1a]">Categories</h2>
                <div className="flex flex-wrap gap-2">
                  {bundle.categories.map((cat, idx) => (
                    <span key={idx} className="px-4 py-2 bg-gray-100 text-[#1a1a1a] rounded-full text-sm font-manrope">
                      {cat}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Pricing & Booking */}
          <div className="space-y-6">
            {/* Pricing Card */}
            <div className="bg-white border border-[#e5e5e5] rounded-xl p-6 sticky top-6">
              <div className="text-center mb-6">
                {bundle.original_price !== bundle.bundle_price && (
                  <p className="text-lg text-[#737373] line-through mb-1">
                    ₹{bundle.original_price?.toLocaleString('en-IN')}
                  </p>
                )}
                <p className="font-mono text-4xl font-bold text-[#D4AF37] mb-1">
                  ₹{bundle.bundle_price?.toLocaleString('en-IN')}
                </p>
                <p className="text-sm text-[#D4AF37]">+ GST</p>
                {discountPercent > 0 && (
                  <p className="text-green-600 font-semibold mt-2">
                    You save ₹{(bundle.original_price - bundle.bundle_price)?.toLocaleString('en-IN')}
                  </p>
                )}
              </div>

              {/* Buy Button */}
              <button
                onClick={handleBuyNow}
                data-testid="buy-now-btn"
                className="w-full py-4 bg-[#D4AF37] text-[#0d0d0d] font-manrope text-sm uppercase tracking-widest hover:bg-[#B8960F] transition-colors rounded-xl flex items-center justify-center gap-2 mb-6"
              >
                <ShoppingCart className="w-5 h-5" />
                Buy Bundle
              </button>

              {/* Trust Badges */}
              <div className="space-y-3 pt-4 border-t border-[#e5e5e5]">
                <div className="flex items-center gap-3 text-sm text-[#737373]">
                  <Shield className="w-5 h-5 text-green-500" />
                  <span>100% Satisfaction Guarantee</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-[#737373]">
                  <Clock className="w-5 h-5 text-blue-500" />
                  <span>Delivery within {bundle.turnaround_days_max} business days</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-[#737373]">
                  <FileText className="w-5 h-5 text-purple-500" />
                  <span>Detailed report with all links</span>
                </div>
              </div>

              {/* Quick Features */}
              {bundle.features && bundle.features.length > 0 && (
                <div className="mt-6 pt-4 border-t border-[#e5e5e5]">
                  <p className="text-xs text-[#737373] uppercase tracking-widest mb-3">Quick Overview:</p>
                  <ul className="space-y-2">
                    {bundle.features.slice(0, 4).map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-[#1a1a1a]">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="line-clamp-1">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Help Card */}
            <div className="bg-[#1a1a1a] text-white rounded-xl p-6">
              <h3 className="font-playfair text-lg font-bold mb-2">Need Help Choosing?</h3>
              <p className="text-gray-400 text-sm mb-4">
                Our PR experts can help you select the right bundle for your goals.
              </p>
              <a 
                href="/contact" 
                className="inline-flex items-center gap-2 text-[#D4AF37] hover:underline font-manrope text-sm"
              >
                Contact Us
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default BundleDetail;
